import React, { useLayoutEffect, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PermissionsGroupId, SponsorBody, SponsorResponse } from '../../../../API';
import TextField from '../../../shared/TextField';
import Button from '../../../shared/Button';
import Drawer from '../../../shared/Drawer';
import { Stack } from '@mui/system';
import { Typography } from '@mui/material';
import { IdBadge } from '../../../shared/IdBadge';
import { sponsorFormTestIds } from '../../../shared/TestsIds';
import { useData } from '../../../../data-layer';
import { useLocales } from '../../../../hooks';
import { useRecoilValue } from 'recoil';
import { makeStyles } from 'tss-react/mui';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { ValidatorForm } from 'react-material-ui-form-validator';
import AssetBrowser from '../../../Assets/AssetBrowser';
import { AssetTypes } from '../../../../utils/assetTypes';
import TextValidator from '../../../shared/TextValidator';
import { URL_REGEX } from '../../../../utils/regex';

const useStyles = makeStyles()((theme) => ({
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4)
  },
  footerButtons: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  footerButton: {
    minWidth: 160
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(4)
  },
  website: {
    width: '100%'
  }
}));

export function SponsorForm(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();

  const {
    sponsors: {
      state: { withFormMetadata, withIsSaving },
      hook: { save: saveSponsor, closeForm: closeSponsorForm }
    }
  } = useData();

  const formMetadata = useRecoilValue(withFormMetadata);
  const isSaving = useRecoilValue(withIsSaving);
  const sponsor = formMetadata.record;
  const formRef = useRef<ValidatorForm>(null);

  const methods = useForm<SponsorBody | SponsorResponse>();
  const { control, handleSubmit, reset } = methods;

  const { canSave } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission } = usePermissions();

  useLayoutEffect(() => {
    reset(sponsor);
  }, [sponsor]);

  const onClose = () => {
    closeSponsorForm();
  };

  const onSubmit = async (sponsor: SponsorBody | SponsorResponse) => {
    const sponsorToSave = { ...sponsor };
    const savedSponsor = await saveSponsor(sponsorToSave);

    if (savedSponsor) {
      onClose();
    }
  };

  const footerLeft = (
    <div className={classes.footerButtons}>
      <Button
        className={classes.footerButton}
        color="primary"
        loading={isSaving}
        onClick={handleSubmit(onSubmit)}
        disabled={!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave}
        data-testid={sponsorFormTestIds.saveButton}
      >
        {t('general.save')}
      </Button>
    </div>
  );

  const footerRight = (
    <Button className={classes.footerButton} color="grey" onClick={onClose}>
      {t('general.cancel')}
    </Button>
  );

  return (
    <Drawer
      open={formMetadata.isShowingForm}
      onClose={onClose}
      headerLeft={
        <Stack direction="row" gap={4}>
          <Typography variant="h6">{t(`payments.${formMetadata.isEditing ? 'edit' : 'new'}_sponsor`)}</Typography>
          {formMetadata.isEditing && sponsor && (
            <IdBadge id={(sponsor as SponsorResponse).id} data-testid={sponsorFormTestIds.sponsorId} />
          )}
        </Stack>
      }
      footerLeft={footerLeft}
      footerRight={footerRight}
      formRef={formRef}
    >
      {sponsor && (
        <div className={classes.formBody} data-testid={sponsorFormTestIds.formBody}>
          <FormProvider {...methods}>
            <div className={classes.formRow}>
              <Controller
                control={control}
                name="name"
                rules={{ required: t('general.field_is_required') }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextField
                    required
                    value={value}
                    onChange={(e) => onChange(e.target.value.trim())}
                    label={t('payments.sponsor.name')}
                    fieldError={error}
                    data-testid={sponsorFormTestIds.nameInput}
                  />
                )}
              />
              <Controller
                control={control}
                name="slogan"
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={(e) => onChange(e.target.value.trim())}
                    label={t('payments.sponsor.slogan')}
                    data-testid={sponsorFormTestIds.sloganInput}
                  />
                )}
              />
            </div>
            <div className={classes.formRow}>
              <Controller
                control={control}
                name="logo"
                rules={{ required: t('general.field_is_required') }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <AssetBrowser
                    label={t('payments.sponsor.logo')}
                    value={value}
                    onChange={onChange}
                    assetType={AssetTypes.sponsor.logo}
                    hasError={!!error}
                    data-testid={sponsorFormTestIds.logoInput}
                    acceptedFileTypes={{ 'image/png': ['.png'] }}
                  />
                )}
              />
              <Controller
                control={control}
                name="website"
                rules={{
                  required: t('general.field_is_required'),
                  pattern: {
                    value: new RegExp(URL_REGEX),
                    message: t('general.field_match_url')
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TextValidator
                    name="website"
                    label={t('payments.sponsor.website')}
                    className={classes.website}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    data-testid={sponsorFormTestIds.website}
                  />
                )}
              />
            </div>
          </FormProvider>
        </div>
      )}
    </Drawer>
  );
}
